export interface SupportTicketItem {
  id: number | string
  subject: string
  status: string
  createdAt: string
  updatedAt: string
  lastMessage: string
  hasUnread: boolean
}

export interface SupportMessageItem {
  id: number | string
  message: string
  createdAt: string
  isStaff: boolean
  authorName: string
}

const asArray = (data: any): any[] => {
  if (Array.isArray(data)) return data
  const list =
    data?.items ?? data?.tickets ?? data?.Tickets ?? data?.results ?? data?.list
  return Array.isArray(list) ? list : []
}

export const normalizeTickets = (data: any): SupportTicketItem[] =>
  asArray(data).map((raw: any) => ({
    id: raw?.id ?? raw?.ticket_id ?? '',
    subject: raw?.subject ?? raw?.title ?? '',
    status: String(raw?.status ?? ''),
    createdAt: raw?.created_at ?? raw?.createdAt ?? '',
    updatedAt: raw?.updated_at ?? raw?.updatedAt ?? raw?.created_at ?? '',
    lastMessage: raw?.last_message ?? raw?.message ?? '',
    hasUnread: Boolean(raw?.has_unread ?? raw?.unread ?? false),
  }))

const messagesArray = (data: any): any[] => {
  if (Array.isArray(data)) return data
  const list =
    data?.items ??
    data?.messages ??
    data?.Messages ??
    data?.replies ??
    data?.ticket?.messages ??
    data?.thread
  return Array.isArray(list) ? list : []
}

const detectStaff = (raw: any): boolean => {
  if (typeof raw?.is_staff === 'boolean') return raw.is_staff
  if (typeof raw?.is_support === 'boolean') return raw.is_support
  if (typeof raw?.is_admin === 'boolean') return raw.is_admin
  const role = raw?.author_type ?? raw?.role ?? raw?.sender ?? raw?.author ?? ''
  return typeof role === 'string' && /support|staff|admin/i.test(role)
}

export const normalizeMessages = (data: any): SupportMessageItem[] =>
  messagesArray(data).map((raw: any, index: number) => ({
    id: raw?.id ?? raw?.message_id ?? index,
    message: raw?.message ?? raw?.text ?? raw?.body ?? '',
    createdAt: raw?.created_at ?? raw?.createdAt ?? '',
    isStaff: detectStaff(raw),
    authorName: raw?.author_name ?? raw?.user_name ?? '',
  }))

export const normalizeTicket = (data: any): SupportTicketItem | null => {
  const raw = data?.ticket ?? data
  if (!raw || typeof raw !== 'object') return null
  const [ticket] = normalizeTickets([raw])
  return ticket ?? null
}

export const formatTicketDate = (iso: string) => {
  if (!iso) return ''
  const date = new Date(iso)
  if (isNaN(date.getTime())) return ''
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`
}

export type TicketStatusKind = 'open' | 'answered' | 'closed'

const ANSWERED_STATUSES = /answer|replied/i
const OPEN_STATUSES = /open|new|wait|pending|process/i

export const getTicketStatusKind = (status: string): TicketStatusKind => {
  if (ANSWERED_STATUSES.test(status)) return 'answered'
  if (!status || OPEN_STATUSES.test(status)) return 'open'
  return 'closed'
}

export const isTicketOpen = (status: string) =>
  getTicketStatusKind(status) !== 'closed'
